import { z } from 'zod'

import { FLUENT_MS } from './games/maths-adaptive.js'
import { skillLabel } from './skillLabels.js'

/**
 * What a parent opens to answer "how is she getting on, and is there anything
 * she is quietly avoiding".
 *
 * The aggregation lives here, as pure functions over rows, so it can be
 * checked against fixtures rather than eyeballed against a live database.
 *
 * Two things it deliberately never does: compare her to another child, and
 * put an age on anything. There is one child using this app and the question
 * is whether she is getting better than she was.
 */

/** One answer, as the parent view needs it. */
export interface AttemptRecord {
  /** Which game asked. Recorded since DOL-49; read by nothing here yet. */
  readonly gameKey: string
  readonly skillKey: string
  readonly prompt: string
  readonly correct: boolean
  readonly elapsedMs: number
  /** ISO, so this stays a plain value that a test can write by hand. */
  readonly answeredAt: string
}

/**
 * How long since a skill was last practised before it is worth mentioning.
 *
 * Three weeks, from the ticket: a skill she has not seen in that long is as
 * interesting as one she gets wrong, and is the shape avoidance takes.
 */
export const RESTING_DAYS = 21

/**
 * How many answers before this says anything with confidence.
 *
 * Below it every skill reports `new`, whatever the numbers look like. Four
 * rounds of a times table can read as mastery or disaster and mean neither,
 * and a confident chart drawn from them is worse than saying "not enough yet".
 */
export const ENOUGH_ATTEMPTS = 8

/** How well a skill is going, in the only terms this app has. */
export type SkillMovement = 'moving' | 'stuck' | 'resting' | 'new'

export interface SkillReport {
  readonly skillKey: string
  /** The parent-facing name, e.g. "The 7 times table". */
  readonly label: string
  readonly attempts: number
  readonly correct: number
  /** Correct *and* quick. Knowing it and working it out are different things. */
  readonly fluent: number
  readonly lastSeenAt: string | null
  readonly movement: SkillMovement
}

export interface TroubleSpot {
  /** What she reads, e.g. "7 × 8". */
  readonly prompt: string
  readonly asked: number
  readonly wrong: number
}

const day = 24 * 60 * 60 * 1000

/** Skills she has answered anything on, newest activity first. */
export function skillReports(
  attempts: readonly AttemptRecord[],
  now: number = Date.now(),
): SkillReport[] {
  const bySkill = new Map<string, AttemptRecord[]>()

  for (const attempt of attempts) {
    const forSkill = bySkill.get(attempt.skillKey) ?? []
    forSkill.push(attempt)
    bySkill.set(attempt.skillKey, forSkill)
  }

  const reports = [...bySkill.entries()].map(([skillKey, forSkill]) => {
    const correct = forSkill.filter((attempt) => attempt.correct)
    const fluent = correct.filter((attempt) => attempt.elapsedMs <= FLUENT_MS)

    const lastSeen = forSkill
      .map((attempt) => Date.parse(attempt.answeredAt))
      .filter((at) => Number.isFinite(at))
      .reduce((latest, at) => Math.max(latest, at), 0)

    return {
      skillKey,
      label: skillLabel(skillKey),
      attempts: forSkill.length,
      correct: correct.length,
      fluent: fluent.length,
      lastSeenAt: lastSeen > 0 ? new Date(lastSeen).toISOString() : null,
      movement: movementOf(forSkill.length, correct.length, lastSeen, now),
    }
  })

  // Newest activity first: what she was doing yesterday is the thing a parent
  // came to look at.
  return reports.sort((a, b) => (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? ''))
}

function movementOf(
  attempts: number,
  correct: number,
  lastSeen: number,
  now: number,
): SkillMovement {
  if (attempts < ENOUGH_ATTEMPTS) return 'new'

  // Checked before the score: a skill she has stopped meeting is the more
  // useful thing to say, even if she was doing well at it when she stopped.
  if (lastSeen > 0 && now - lastSeen > RESTING_DAYS * day) return 'resting'

  // Two thirds is not a cliff, it is a line drawn somewhere. It is here rather
  // than higher because these questions are chosen to stretch her: getting a
  // third of them wrong is the adaptive difficulty working, not a problem.
  return correct / attempts >= 2 / 3 ? 'moving' : 'stuck'
}

/**
 * The questions she gets wrong again and again.
 *
 * By prompt rather than by skill, because "7 × 8 comes up wrong about half the
 * time" is something a parent can do something about on a car journey, where
 * "multiply.7: 61%" is a statistic.
 */
export function troubleSpots(
  attempts: readonly AttemptRecord[],
  { atLeast = 2, limit = 8 }: { atLeast?: number; limit?: number } = {},
): TroubleSpot[] {
  const byPrompt = new Map<string, { asked: number; wrong: number }>()

  for (const attempt of attempts) {
    const tally = byPrompt.get(attempt.prompt) ?? { asked: 0, wrong: 0 }
    tally.asked += 1
    if (!attempt.correct) tally.wrong += 1
    byPrompt.set(attempt.prompt, tally)
  }

  return (
    [...byPrompt.entries()]
      .map(([prompt, tally]) => ({ prompt, ...tally }))
      // Wrong more than once: everybody slips, and a single miss is not a
      // trouble spot. Repetition is what makes it worth a parent's attention.
      .filter((spot) => spot.wrong >= atLeast)
      .sort((a, b) => b.wrong - a.wrong || b.asked - a.asked || a.prompt.localeCompare(b.prompt))
      .slice(0, limit)
  )
}

/** Whether there is enough here to say anything at all. */
export function enoughToSay(attempts: readonly AttemptRecord[]): boolean {
  return attempts.length >= ENOUGH_ATTEMPTS
}

export const attemptRecordSchema = z
  .object({
    gameKey: z.string(),
    skillKey: z.string(),
    prompt: z.string(),
    correct: z.boolean(),
    elapsedMs: z.number().int().min(0),
    answeredAt: z.string(),
  })
  .strict()

/** How a child's reading has gone, per band. */
export const readingBandReportSchema = z
  .object({
    band: z.string(),
    rounds: z.number().int().min(0),
    lastPlayedAt: z.string().nullable(),
  })
  .strict()

export const childReportSchema = z
  .object({
    userId: z.string(),
    displayName: z.string(),
    avatarKey: z.string().nullable(),
    /** Solo rounds, which are the only ones that move her levels. */
    soloRounds: z.number().int().min(0),
    /**
     * Rounds played in a room.
     *
     * Counted and labelled, never folded in: a maths race runs at one level
     * for everybody so it says nothing about where hers should be, which is
     * exactly why a race does not move them.
     */
    roomRounds: z.number().int().min(0),
    attempts: z.number().int().min(0),
    enough: z.boolean(),
    skills: z.array(
      z
        .object({
          skillKey: z.string(),
          label: z.string(),
          attempts: z.number().int().min(0),
          correct: z.number().int().min(0),
          fluent: z.number().int().min(0),
          lastSeenAt: z.string().nullable(),
          movement: z.enum(['moving', 'stuck', 'resting', 'new']),
        })
        .strict(),
    ),
    trouble: z.array(
      z
        .object({
          prompt: z.string(),
          asked: z.number().int().min(0),
          wrong: z.number().int().min(0),
        })
        .strict(),
    ),
    reading: z.array(readingBandReportSchema),
  })
  .strict()

export type ChildReport = z.infer<typeof childReportSchema>

export const parentReportSchema = z.object({ children: z.array(childReportSchema) }).strict()

export type ParentReport = z.infer<typeof parentReportSchema>
